import { useCallback, useReducer, useRef, useState } from 'react'

type IEventOption<T extends string | number> = {
  label: string | JSX.Element | ReactElement | ReactNode
  value: T
  [k: string]: any
}

type IEventActionType<E extends string> = `${E}:add` | `${E}:remove` | `${E}:clear` | 'clearAll'

type IEventExecutor<D extends Record<string, any> = Record<string, any> > = (data: D) => void

type IEventAction<E extends string, D extends Record<string, any> = Record<string, any>> = {
  type: IEventActionType<E>
  executor: IEventExecutor<D>
}


const useEventStore = <E extends string, D extends Record<string, any>>(
  events: E[],
  data: D = {} as D,
) => {
  const _evtStore: Map<E, Set<IEventExecutor<D>>> = new Map()

  const eventHandlerReducer = useCallback((state: Map<E, Set<IEventExecutor<D>>>, action: IEventAction<E, D>) => {
    const { type, executor } = action
    if (type === 'clearAll') {
      state.clear()
      return state
    }
    const [evt] = type.split(':')
    const evtName = evt as E
    if (!state.has(evtName)) {
      state.set(evtName, new Set())
    }
    const evtSet = state.get(evtName)
    if (evtSet?.has(executor)) {
      return state
    }
    if (type === `${evt}:add`) {
      evtSet?.add(executor)
      return state
    }
    if (type === `${evt}:clear`) {
      evtSet?.clear()
      return state
    }
    if (type === `${evt}:remove`) {
      evtSet?.delete(executor)
      return state
    }
    return state
  }, [])

  const [evtStore, dispatchEvtStore] = useReducer(eventHandlerReducer, _evtStore)

  const storeRef = useRef(Object.freeze(data))

  const updateStore = useCallback(
    (source: D | ((ret: D) => D)) => {
      let ret = typeof source === 'function' ? source(storeRef.current) : source
      ret = Object.freeze(ret)
      storeRef.current = ret
    },
    [storeRef],
  )

  const on = (evt: E, cb: IEventExecutor<D>) => {
    dispatchEvtStore({
      type: `${evt}:add`,
      executor: cb,
    })
    return {
      off: () => {
        dispatchEvtStore({
          type: `${evt}:remove`,
          executor: cb,
        })
      },
      on,
    }
  }

  const emit = useCallback((type: E) => {
    const evtSet = evtStore.get(type)
    if (!evtSet) {
      return
    }
    evtSet.forEach((executor) => {
      executor(storeRef.current)
    })
  }, [evtStore])

  const clear = useCallback((type: E) => {
    dispatchEvtStore({
      type: `${type}:clear`,
      executor: () => {},
    })
  }, [])

  const clearAll = useCallback(() => {
    dispatchEvtStore({
      type: 'clearAll',
      executor: () => {},
    })
  }, [])

  const flushWith = useCallback((event: E, row: D) => {
    if (!event || !events.includes(event)) return
    updateStore(row)
    emit(event)
  }, [emit, events, updateStore])

  return {
    on,
    emit,
    clear,
    flushWith,
    updateStore,
    clearAll
  }
}

const useOptionsEvent = <D extends Record<string, any>, T extends string>(options: IEventOption<T>[], data?: D) => {
  const optionFieldSupplyHandlerRef = useRef<typeof supplyOptionField>()

  const eventsRef = useRef<T[]>([])
  const optionsRef = useRef<IComboboxOption<T>[]>(options as IComboboxOption<T>[])

  useState(() => {
    const opts = options.map((option) => {
      option.id = option.value
      eventsRef.current.push(option.value)
      return option as IComboboxOption<T>
    })
    optionsRef.current = opts
  })

  const eventStore = useEventStore<T, D>(eventsRef.current, data)

  const getOptions = useCallback(() => {
    return Array.from(optionsRef.current)
  }, [])

  const supplyOptionField = (v: T, newField: ((option: IComboboxOption<T>) => Record<string, any>)) => {
    const optionsList = optionsRef.current
    const newOptions = optionsList.flatMap<IComboboxOption<T>>((opt) => {
      if (opt.value !== v) return opt
      const newOpt = newField(opt)
      return {...opt, ...newOpt}
    })
    optionsRef.current = newOptions
    return {
      get: getOptions,
      supplyOptionField
    }
  }

  optionFieldSupplyHandlerRef.current = supplyOptionField

  return {
    ...eventStore,
    getOptions,
    supplyOptionField: optionFieldSupplyHandlerRef.current
  }
}

export {
  useEventStore,
  useOptionsEvent,
}